import { normalizePhone } from '@/lib/phone';

/**
 * ניתוח קובץ עובדים לייבוא (PRD סעיף 4.1).
 *
 * הניתוח כולו מופרד מהממשק ומהשרת, כדי שאפשר יהיה לבדוק אותו על
 * קבצים אמיתיים בלי דפדפן ובלי בסיס נתונים.
 */

export type ImportColumn =
  | 'first_name'
  | 'last_name'
  | 'phone'
  | 'branch'
  | 'job_title'
  | 'start_date'
  | 'birth_date'
  | 'email'
  | 'employee_code';

/**
 * כותרות מקובלות לכל עמודה. אלה שמות של עמודות בקובץ חיצוני, לא
 * תוכן שהמשתמש מנהל, ולכן מקומם בקוד (ולא סותר את PRD 2.4).
 */
const HEADERS: Record<ImportColumn, string[]> = {
  first_name: ['שם פרטי', 'שם', 'first name', 'firstname'],
  last_name: ['שם משפחה', 'משפחה', 'last name', 'lastname'],
  phone: ['טלפון', 'נייד', 'מספר טלפון', 'phone', 'mobile'],
  branch: ['סניף', 'branch'],
  job_title: ['תפקיד', 'job title', 'role'],
  start_date: ['תאריך התחלה', 'תאריך תחילת עבודה', 'start date'],
  birth_date: ['תאריך לידה', 'birth date', 'birthday'],
  email: ['מייל', 'אימייל', 'דוא"ל', 'email'],
  // הקוד מקומקס ומהקופה. איתו מקשרים נתוני מכירות לעובד
  employee_code: ['קוד עובד', 'מספר עובד', 'קוד', 'employee code', 'employee id'],
};

const REQUIRED: ImportColumn[] = ['first_name', 'last_name', 'phone'];

export type ParsedRow = {
  /** מספר השורה בקובץ, כולל הכותרת, כדי שאפשר יהיה להפנות אליה */
  line: number;
  first_name: string;
  last_name: string;
  /** מנורמל ל E.164, או null אם לא ניתן לנרמול */
  phone: string | null;
  rawPhone: string;
  branch: string;
  job_title: string;
  start_date: string | null;
  birth_date: string | null;
  email: string | null;
  employee_code: string | null;
  errors: string[];
};

export type ParseResult = {
  rows: ParsedRow[];
  /** עמודות חובה שלא נמצאו בקובץ */
  missingColumns: ImportColumn[];
  /** כותרות שלא זוהו, לתצוגה למשתמש */
  unknownHeaders: string[];
};

const LABELS: Record<ImportColumn, string> = {
  first_name: 'שם פרטי',
  last_name: 'שם משפחה',
  phone: 'טלפון',
  branch: 'סניף',
  job_title: 'תפקיד',
  start_date: 'תאריך התחלה',
  birth_date: 'תאריך לידה',
  email: 'מייל',
  employee_code: 'קוד עובד',
};

export const columnLabel = (column: ImportColumn) => LABELS[column];

/** מפצל שורת CSV אחת, עם תמיכה במרכאות ובפסיקים בתוכן. */
export function splitCsvLine(line: string, delimiter: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      cells.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  cells.push(current);
  return cells.map((cell) => cell.trim());
}

function detectDelimiter(headerLine: string): string {
  const counts = [',', ';', '\t'].map((d) => ({ d, n: headerLine.split(d).length }));
  counts.sort((a, b) => b.n - a.n);
  return counts[0].n > 1 ? counts[0].d : ',';
}

function matchColumn(header: string): ImportColumn | null {
  // trim מסיר גם BOM, שמופיע בתחילת קבצים שנשמרו מ Excel
  const clean = header.trim().toLowerCase();
  for (const [column, names] of Object.entries(HEADERS) as [ImportColumn, string[]][]) {
    if (names.some((name) => name.toLowerCase() === clean)) return column;
  }
  return null;
}

/**
 * תאריכים מגיעים בפורמט ישראלי (dd.MM.yyyy או dd/MM/yyyy) או ISO.
 * מוחזר yyyy-MM-dd, או null כשאי אפשר לקרוא.
 */
export function parseDate(value: string): string | null {
  const text = value.trim();
  if (!text) return null;

  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (iso) return text;

  const local = /^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/.exec(text);
  if (!local) return null;

  const day = Number(local[1]);
  const month = Number(local[2]);
  let year = Number(local[3]);
  if (year < 100) year += year < 50 ? 2000 : 1900;

  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;

  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function parseEmployeesCsv(text: string): ParseResult {
  const lines = text
    .split(/\r?\n/)
    .filter((line) => line.trim() !== '');

  if (lines.length === 0) {
    return { rows: [], missingColumns: REQUIRED, unknownHeaders: [] };
  }

  const delimiter = detectDelimiter(lines[0]);
  const headers = splitCsvLine(lines[0], delimiter);

  const index = {} as Record<ImportColumn, number>;
  const unknownHeaders: string[] = [];
  headers.forEach((header, position) => {
    const column = matchColumn(header);
    if (column === null) {
      if (header !== '') unknownHeaders.push(header);
    } else if (index[column] === undefined) {
      index[column] = position;
    }
  });

  const missingColumns = REQUIRED.filter((column) => index[column] === undefined);
  if (missingColumns.length > 0) return { rows: [], missingColumns, unknownHeaders };

  const cell = (cells: string[], column: ImportColumn) =>
    index[column] === undefined ? '' : (cells[index[column]] ?? '').trim();

  const seen = new Set<string>();
  const rows = lines.slice(1).map((line, offset): ParsedRow => {
    const cells = splitCsvLine(line, delimiter);
    const errors: string[] = [];

    const first_name = cell(cells, 'first_name');
    const last_name = cell(cells, 'last_name');
    const rawPhone = cell(cells, 'phone');
    const phone = normalizePhone(rawPhone);

    if (!first_name) errors.push('חסר שם פרטי');
    if (!last_name) errors.push('חסר שם משפחה');
    if (!rawPhone) errors.push('חסר טלפון');
    else if (!phone) errors.push('טלפון לא תקין');
    else if (seen.has(phone)) errors.push('הטלפון מופיע פעמיים בקובץ');

    if (phone) seen.add(phone);

    const startRaw = cell(cells, 'start_date');
    const start_date = parseDate(startRaw);
    if (startRaw && !start_date) errors.push('תאריך התחלה לא תקין');

    const birthRaw = cell(cells, 'birth_date');
    const birth_date = parseDate(birthRaw);
    if (birthRaw && !birth_date) errors.push('תאריך לידה לא תקין');

    return {
      line: offset + 2,
      first_name,
      last_name,
      phone,
      rawPhone,
      branch: cell(cells, 'branch'),
      employee_code: cell(cells, 'employee_code') || null,
      job_title: cell(cells, 'job_title'),
      start_date,
      birth_date,
      email: cell(cells, 'email') || null,
      errors,
    };
  });

  return { rows, missingColumns: [], unknownHeaders };
}

/**
 * משייך שמות סניף ותפקיד למזהים. שם שלא נמצא הוא שגיאה בשורה,
 * כי ייבוא לא יוצר סניפים או תפקידים חדשים בשקט.
 */
export function resolveReferences(
  rows: ParsedRow[],
  branches: { id: string; name: string }[],
  jobTitles: { id: string; name: string }[],
  existingPhones: Set<string>,
): (ParsedRow & { branch_id: string | null; job_title_id: string | null })[] {
  const byName = (list: { id: string; name: string }[]) =>
    new Map(list.map((item) => [item.name.trim(), item.id]));

  const branchMap = byName(branches);
  const jobMap = byName(jobTitles);

  return rows.map((row) => {
    const errors = [...row.errors];

    let branch_id: string | null = null;
    if (row.branch) {
      branch_id = branchMap.get(row.branch) ?? null;
      if (!branch_id) errors.push(`הסניף "${row.branch}" לא קיים`);
    }

    let job_title_id: string | null = null;
    if (row.job_title) {
      job_title_id = jobMap.get(row.job_title) ?? null;
      if (!job_title_id) errors.push(`התפקיד "${row.job_title}" לא קיים`);
    }

    if (row.phone && existingPhones.has(row.phone)) {
      errors.push('הטלפון כבר קיים במערכת');
    }

    return { ...row, branch_id, job_title_id, errors };
  });
}
